// React
import { useState, useRef, useEffect } from "react";
import { useForm } from "react-hook-form";

// Icon
import { IoFilterSharp, IoClose, IoAdd } from "react-icons/io5";
import { IoIosSearch } from "react-icons/io";
import { FiUser, FiLayers, FiFlag, FiClock, FiMapPin, FiCalendar, FiHash } from "react-icons/fi";

// Utility
import { translate } from "../../../../../utils/Common";

// Component
import TypeIncidentSelect from "../../TypeIncidentSelect";
import PriorityIncidentList from "../../PriorityIncidentList";
import StatusIncidentList from "../../StatusIncidentList";
import DateIncident from "../../DateIncident";
import ClientIncidentSelect from "../../ClientIncidentSelect";
import PlatformIncidentSelect from "../../PlatformIncidentSelect";

// CSS
import "./index.css";
import AssignedUserSelect from "../../AssignedUserSelect";
import Spinner from "../../../../../components/Spinner";
import Button from "../../../../../components/common/Button";

const FilterIncident = (props) => {
  const { filterOptions, setFilterOptions, isLoading } = props;

  const { register, errors, handleSubmit, setValue } = useForm();
  const [viewFilter, setViewFilter] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [assignedUserId, setAssignedUserId] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [platformId, setPlatformId] = useState(null);
  const [incidentIdInput, setIncidentIdInput] = useState("");
  const [activeFilters, setActiveFilters] = useState(0);
  const dropdownRef = useRef(null);

  // Contar filtros activos
  useEffect(() => {
    let count = 0;
    
    // Contar filtros de fecha (startDate + endDate = 1 filtro)
    if (filterOptions.startDate && filterOptions.startDate.length > 0) {
      count += 1;
    }
    
    // Contar otros filtros
    const otherFilters = ['typeIncident', 'priorityIncident', 'statusIncident', 'assignedUser', 'clientId', 'platformId', 'incidentId'];
    otherFilters.forEach(key => {
      if (filterOptions[key] && Array.isArray(filterOptions[key]) && filterOptions[key].length > 0) {
        count += filterOptions[key].length;
      }
    });
    
    setActiveFilters(count);
  }, [filterOptions]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Los menus de react-select se renderizan con menuPortalTarget={document.body},
      // asi que viven FUERA de dropdownRef. Sin este guard, hacer click en una opcion
      // (ej. el filtro de Cliente) contaba como click afuera y cerraba el panel antes
      // de que react-select alcanzara a disparar su onChange.
      const clickedInPortaledMenu =
        typeof event.target?.closest === "function" &&
        event.target.closest(".react-select__menu-portal, .react-select__menu");
      if (clickedInPortaledMenu) return;

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setViewFilter(false);
        setSelectedOption(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCloseView = () => {
    setViewFilter(!viewFilter);
    setSelectedOption(null);
  };

  const filterKeyByOptionId = {
    1: "typeIncident",
    2: "priorityIncident",
    4: "startDate",
    5: "assignedUser",
    6: "clientId",
    7: "platformId",
    8: "incidentId",
  };

  const handleApplyFilter = (e) => {
    if (!selectedOption) return;
    const filterKey = filterKeyByOptionId[selectedOption];
    if (!filterKey) return;
    // Incident Id: usar estado local para no depender del form
    if (filterKey === "incidentId") {
      e?.preventDefault?.();
      const raw = incidentIdInput?.toString().trim() ?? "";
      const num = raw !== "" ? Number(raw) : NaN;
      if (!Number.isNaN(num) && num > 0) {
        handleToggleOption({ incidentId: num });
        setViewFilter(false);
        setSelectedOption(null);
        setIncidentIdInput("");
      }
      return;
    }
    handleSubmit(submitFilter)(e);
  };

  const submitFilter = (data) => {
    if (!selectedOption || Object.keys(data).length === 0) return;
    const filterKey = filterKeyByOptionId[selectedOption];
    if (!filterKey) return;
    const option =
      filterKey === "startDate"
        ? { startDate: data.startDate, endDate: data.endDate }
        : filterKey === "clientId"
        ? { clientId }
        : filterKey === "platformId"
        ? { platformId }
        : filterKey === "assignedUser"
        ? { assignedUser: assignedUserId }
        : { [filterKey]: data[filterKey] };
    if (Object.values(option).some((v) => v !== undefined && v !== null && v !== "")) {
      handleToggleOption(option);
    }
  };

  const optionsSelect = [
    {
      id: 8,
      name: translate("global.incidentId"),
      icon: <FiHash size={16} />,
      component: (
        <div className="mb-3">
          <input
            type="number"
            inputMode="numeric"
            className="h-11 rounded-lg border border-transparent bg-muted/60 text-sm w-full px-3 text-foreground placeholder:text-muted-foreground/70 focus-visible:outline-none focus-visible:border-primary focus-visible:bg-background focus-visible:ring-4 focus-visible:ring-primary/15 disabled:bg-transparent disabled:text-muted-foreground disabled:cursor-not-allowed"
            placeholder={translate("global.enterIncidentId")}
            value={incidentIdInput}
            onChange={(e) => setIncidentIdInput(e.target.value)}
          />
        </div>
      ),
    },
    {
      id: 1,
      name: translate("global.type"),
      icon: <FiLayers size={16} />,
      component: (
        <TypeIncidentSelect
          errors={errors}
          register={register}
          setValue={setValue}
          nameShow={translate("global.select")}
          readOnly={false}
          sizeInput={"form-control-md"}
        />
      ),
    },
    {
      id: 2,
      name: translate("global.priority"),
      icon: <FiFlag size={16} />,
      component: (
        <PriorityIncidentList
          errors={errors}
          register={register}
          setValue={setValue}
          nameShow={translate("global.select")}
          readOnly={false}
          sizeInput={"form-control-md"}
        />
      ),
    },
    // {
    //   id: 3,
    //   name: translate("global.state"),
    //   icon: <FiClock size={16} />,
    //   component: (
    //     <StatusIncidentList
    //       errors={errors}
    //       register={register}
    //       nameShow={translate("global.select")}
    //       readOnly={false}
    //       sizeInput={"form-control-md"}
    //     />
    //   ),
    // },
    {
      id: 4,
      name: translate("global.dateCreated"),
      icon: <FiCalendar size={16} />,
      component: (
        <DateIncident
          errors={errors}
          register={register}
          nameShow={translate("global.select")}
          readOnly={false}
          sizeInput={"form-control-md"}
        />
      ),
    },
    {
      id: 5,
      name: translate("global.assignedTechnician"),
      icon: <FiUser size={16} />,
      component: (
        <AssignedUserSelect
          assignedUserId={assignedUserId}
          register={register}
          errors={errors}
          onChange={(val) => setAssignedUserId(val)}
        />
      )
    },
    {
      id: 6,
      name: translate("global.client"),
      icon: <FiUser size={16} />,
      component: (
        <ClientIncidentSelect
          errors={errors}
          register={register}
          nameShow={translate("global.select")}
          readOnly={false}
          sizeInput={"form-control-md"}
          setClientId={setClientId}
        />
      ),
    },
    {
      id: 7,
      name: translate("global.locker"),
      icon: <FiMapPin size={16} />,
      component: (
        <PlatformIncidentSelect
          errors={errors}
          register={register}
          nameShow={translate("global.select")}
          readOnly={false}
          sizeInput={"form-control-md"}
          setPlatformId={setPlatformId}
        />
      ),
    }
  ];

  const renderComponent = () => {
    const selected = optionsSelect.find(
      (option) => option.id === selectedOption
    );
    return selected ? selected.component : null;
  };

  const handleToggleOption = (option) => {
    const filterKey = Object.keys(option)[0];
    const newOption = option[filterKey];

    let newFilterOptions = { ...filterOptions };

    if (!filterOptions[filterKey] || !filterOptions[filterKey].includes(Number(newOption))) {
      if (filterKey === "startDate") {
        newFilterOptions[filterKey] = [newOption];
        newFilterOptions["endDate"] = [option.endDate];
      } else if (filterKey === "clientId") {
        newFilterOptions[filterKey] = [clientId];
      } else if (filterKey === "platformId") {
        newFilterOptions[filterKey] = [platformId];
      } else if (filterKey === "incidentId") {
        newFilterOptions[filterKey] = [Number(newOption)];
      } else {
        // Asegurar que el array existe antes de hacer push
        if (!newFilterOptions[filterKey]) {
          newFilterOptions[filterKey] = [];
        }
        newFilterOptions[filterKey].push(Number(newOption));
      }

      /**
       * Siempre que se aplique un filtro desde "Filter by"
       * limpiamos el filtro de status para que la búsqueda
       * se haga sobre todos los estados disponibles.
       */
      if (filterKey !== "statusIncident") {
        newFilterOptions["statusIncident"] = [];
      }
    }

    setFilterOptions(newFilterOptions);

    setTimeout(() => {
      setViewFilter(false);
    }, 2000);
  };

  const renderBtnFilter = (
    <div className="modern-filter-container" ref={dropdownRef}>
      <div className="filter-trigger-container">
        <Button
          variant="primary"
          size="md"
          leftIcon={<IoFilterSharp size={18} />}
          onClick={handleCloseView}
          className={viewFilter ? 'filter-btn-active' : ''}
        >
          {translate("global.filterBy")}
          {activeFilters > 0 && (
            <span className="filter-badge">{activeFilters}</span>
          )}
        </Button>
      </div>

      {viewFilter && (
        <div className="modern-filter-dropdown">
          <div className="filter-header">
            <h4>{translate("global.filterBy")}</h4>
            <button 
              className="close-btn"
              onClick={() => {
                setViewFilter(false);
                setSelectedOption(null);
              }}
            >
              <IoClose size={20} />
            </button>
          </div>

          <div className="filter-main-content">
            {selectedOption && (
              <div className="filter-content">
                <div className="filter-content-header">
                  <div className="filter-content-title">
                    {optionsSelect.find(opt => opt.id === selectedOption)?.icon}
                    <span>{optionsSelect.find(opt => opt.id === selectedOption)?.name}</span>
                  </div>
                  <button 
                    className="close-content-btn"
                    onClick={() => setSelectedOption(null)}
                  >
                    <IoClose size={16} />
                  </button>
                </div>
                <div className="filter-content-body">
                  {renderComponent()}
                </div>
                <div className="filter-content-footer">
                  <button
                    type="button"
                    className="apply-filter-btn"
                    onClick={handleApplyFilter}
                  >
                    {!isLoading ? (
                      <>
                        <IoIosSearch size={16} />
                        <span>{translate("global.applyFilter")}</span>
                      </>
                    ) : (
                      <Spinner type={1} size={16} />
                    )}
                  </button>
                </div>
              </div>
            )}

            <div className="filter-options-grid">
              {optionsSelect.map((option) => (
                <div
                  key={option.id}
                  className={`filter-option-card ${selectedOption === option.id ? 'selected' : ''}`}
                  onClick={() => setSelectedOption(selectedOption === option.id ? null : option.id)}
                >
                  <div className="filter-option-icon">
                    {option.icon}
                  </div>
                  <span className="filter-option-name">{option.name}</span>
                  <IoAdd className="filter-option-add" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return <div>{renderBtnFilter}</div>;
};

export default FilterIncident;
